/**
 * Environment Configuration
 *
 * Validates and exports environment variables with type safety
 */

interface EnvironmentConfig {
  apiUrl: string
  environment: 'development' | 'staging' | 'production'
  enableDevTools: boolean
  enableWebSocket: boolean
  wsUrl: string
  logLevel: 'debug' | 'info' | 'warn' | 'error'
  version: string
}

/**
 * Get environment variable with validation
 */
function getEnvVar(
  key: string,
  defaultValue?: string,
  required: boolean = false
): string {
  const value = import.meta.env[key] || defaultValue

  if (required && !value) {
    throw new Error(
      `Missing required environment variable: ${key}. ` +
      `Please check your .env file or environment configuration.`
    )
  }

  return value || ''
}

/**
 * Validate environment configuration
 */
function validateConfig(): EnvironmentConfig {
  // Get mode from Vite
  const mode = import.meta.env.MODE || 'development'

  // API URL - in production, empty string means same-origin (nginx proxies /api/)
  const apiUrl = getEnvVar(
    'VITE_API_URL',
    mode === 'production' ? '' : 'http://localhost:8000',
    false
  )

  // WebSocket URL - derived from API URL if not specified
  let wsUrl = getEnvVar('VITE_WS_URL', '')
  if (!wsUrl) {
    if (apiUrl) {
      // Convert HTTP URL to WS URL
      wsUrl = apiUrl.replace(/^http/, 'ws')
    } else {
      // Same-origin: derive from current location
      const wsProt = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      wsUrl = typeof window !== 'undefined' ? `${wsProt}//${window.location.host}` : ''
    }
  }

  // Determine environment
  let environment: 'development' | 'staging' | 'production' = 'development'
  if (mode === 'production') {
    environment = 'production'
  } else if (mode === 'staging') {
    environment = 'staging'
  }

  // Enable dev tools only in development
  const enableDevTools = mode === 'development'

  // Enable WebSocket
  const enableWebSocket = getEnvVar('VITE_ENABLE_WEBSOCKET', 'true') === 'true'

  // Log level
  const logLevel = (getEnvVar('VITE_LOG_LEVEL', 'info') as any) || 'info'
  if (!['debug', 'info', 'warn', 'error'].includes(logLevel)) {
    console.warn(`Invalid log level: ${logLevel}, defaulting to 'info'`)
  }

  // Version from package.json or env
  const version = getEnvVar('VITE_APP_VERSION', '1.0.0')

  const config: EnvironmentConfig = {
    apiUrl,
    environment,
    enableDevTools,
    enableWebSocket,
    wsUrl,
    logLevel: ['debug', 'info', 'warn', 'error'].includes(logLevel) ? logLevel : 'info',
    version,
  }

  // Log configuration in development
  if (mode === 'development') {
    console.log('Environment Configuration:', {
      ...config,
      apiUrl: config.apiUrl,
      wsUrl: config.wsUrl,
    })
  }

  // Validate URLs (skip for empty string = same-origin in production)
  if (config.apiUrl) {
    try {
      new URL(config.apiUrl)
    } catch (error) {
      throw new Error(`Invalid API URL: ${config.apiUrl}`)
    }
  }

  return config
}

/**
 * Export validated configuration
 */
export const env = validateConfig()

/**
 * Check if running in development
 */
export const isDevelopment = env.environment === 'development'

/**
 * Check if running in production
 */
export const isProduction = env.environment === 'production'

/**
 * Check if running in staging
 */
export const isStaging = env.environment === 'staging'

/**
 * Get full API endpoint
 */
export function getApiEndpoint(path: string): string {
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path

  // Ensure API URL doesn't end with slash
  const baseUrl = env.apiUrl.endsWith('/')
    ? env.apiUrl.slice(0, -1)
    : env.apiUrl

  return `${baseUrl}/${cleanPath}`
}

/**
 * Get WebSocket endpoint
 */
export function getWsEndpoint(path: string): string {
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path

  // Ensure WS URL doesn't end with slash
  const baseUrl = env.wsUrl.endsWith('/')
    ? env.wsUrl.slice(0, -1)
    : env.wsUrl

  return `${baseUrl}/${cleanPath}`
}

/**
 * Feature flags
 */
export const features = {
  websocket: env.enableWebSocket,
  devTools: env.enableDevTools,
  analytics: isProduction,
  errorReporting: isProduction || isStaging,
}

/**
 * Logger utility
 */
export const logger = {
  debug: (...args: any[]) => {
    if (env.logLevel === 'debug' && !isProduction) {
      console.log('[DEBUG]', ...args)
    }
  },
  info: (...args: any[]) => {
    if (['debug', 'info'].includes(env.logLevel)) {
      console.info('[INFO]', ...args)
    }
  },
  warn: (...args: any[]) => {
    if (['debug', 'info', 'warn'].includes(env.logLevel)) {
      console.warn('[WARN]', ...args)
    }
  },
  error: (...args: any[]) => {
    console.error('[ERROR]', ...args)
  },
}

export default env
